import { attachmentMoveRefusal, canUserRenameAttachment } from '../../storage-folders/files';
import { attachmentFolderId, homeFolderId, isSystemFolderId } from '../../storage-folders/system';
import { isGeneralFolder, selectFolderAncestors, storageFolderChildren } from '../../storage-folders/tree';
import type { Attachment, AttachmentId, MockCache, StorageFolder, StorageFolderId } from '../../types';

import { maxSortKey, sortKeyBetween } from '../internal';
import { bump, requireCache } from '../runtime';

import { attachmentOwnerKeys, requireAttachmentName } from './comments';

export const STORAGE_FOLDERS_KEY = 'storage-folders';

const MAX_FOLDER_NAME_LENGTH = 120;

function requireFolderName(raw: string): string {
  const name = raw.trim();
  if (name.length === 0) {
    throw new Error('Название папки не может быть пустым');
  }
  if (name.length > MAX_FOLDER_NAME_LENGTH) {
    throw new Error(`Название папки длиннее ${MAX_FOLDER_NAME_LENGTH} знаков`);
  }
  return name;
}

function requireEditableFolder(cache: MockCache, id: StorageFolderId): StorageFolder {
  if (isSystemFolderId(id)) {
    throw new Error('Системную папку нельзя изменить');
  }
  const folder = cache.storageFolders.get(id);
  if (folder === undefined || folder.workspaceId !== cache.activeWorkspaceId) {
    throw new Error('Папка не найдена');
  }
  return folder;
}

function requireParent(cache: MockCache, parentId: StorageFolderId | null): void {
  if (parentId === null) {
    return;
  }
  if (isSystemFolderId(parentId)) {
    throw new Error('В системную папку нельзя положить папку');
  }
  if (!isGeneralFolder(cache, parentId)) {
    throw new Error('Папка не найдена');
  }
}

function lastSortKey(cache: MockCache, parentId: StorageFolderId | null, exceptId: StorageFolderId | null): string {
  const siblings = storageFolderChildren(cache, parentId).filter((folder) => folder.id !== exceptId);
  return sortKeyBetween(maxSortKey(siblings.map((folder) => folder.sortKey)), null);
}

function requireAttachment(cache: MockCache, id: AttachmentId): Attachment {
  const attachment = cache.attachments.get(id);
  if (attachment === undefined) {
    throw new Error('Файл не найден');
  }
  return attachment;
}

export function createStorageFolder(args: { id: StorageFolderId; parentId: StorageFolderId | null; name: string }): StorageFolder {
  const cache = requireCache();
  const name = requireFolderName(args.name);
  if (cache.storageFolders.has(args.id)) {
    throw new Error('Папка с таким идентификатором уже есть');
  }
  requireParent(cache, args.parentId);
  const folder: StorageFolder = {
    id: args.id,
    workspaceId: cache.activeWorkspaceId,
    parentId: args.parentId,
    name,
    sortKey: lastSortKey(cache, args.parentId, null),
    createdAt: cache.nowMs(),
    createdBy: cache.me,
  };
  cache.storageFolders.set(folder.id, folder);
  bump(STORAGE_FOLDERS_KEY);
  return folder;
}

export function renameStorageFolder(args: { id: StorageFolderId; name: string }): void {
  const cache = requireCache();
  const folder = requireEditableFolder(cache, args.id);
  folder.name = requireFolderName(args.name);
  bump(STORAGE_FOLDERS_KEY);
}

export function moveStorageFolder(args: { id: StorageFolderId; parentId: StorageFolderId | null }): void {
  const cache = requireCache();
  const folder = requireEditableFolder(cache, args.id);
  requireParent(cache, args.parentId);
  const intoOwnSubtree =
    args.parentId !== null &&
    (args.parentId === folder.id || selectFolderAncestors(cache, args.parentId).some((ancestor) => ancestor.id === folder.id));
  if (intoOwnSubtree) {
    throw new Error('Папку нельзя перенести в саму себя или в свою вложенную папку');
  }
  folder.sortKey = lastSortKey(cache, args.parentId, folder.id);
  folder.parentId = args.parentId;
  bump(STORAGE_FOLDERS_KEY);
}

export function removeStorageFolder(args: { id: StorageFolderId }): void {
  const cache = requireCache();
  const folder = requireEditableFolder(cache, args.id);
  const hasFolders = storageFolderChildren(cache, folder.id).length > 0;
  const hasFiles = [...cache.attachments.values()].some((attachment) => attachmentFolderId(attachment) === folder.id);
  if (hasFolders || hasFiles) {
    throw new Error('Папка не пуста');
  }
  cache.storageFolders.delete(folder.id);
  bump(STORAGE_FOLDERS_KEY);
}

export function renameAttachment(args: { id: AttachmentId; name: string }): void {
  const cache = requireCache();
  const attachment = requireAttachment(cache, args.id);
  const name = requireAttachmentName(args.name);
  if (!canUserRenameAttachment(cache, cache.me, attachment)) {
    throw new Error('Файл нельзя переименовать');
  }
  if (attachment.name === name) {
    return;
  }
  attachment.name = name;
  bump(...attachmentOwnerKeys(cache, attachment, undefined));
}

export function moveAttachmentToFolder(args: { id: AttachmentId; folderId: StorageFolderId | null }): void {
  const cache = requireCache();
  const attachment = requireAttachment(cache, args.id);
  const refusal = attachmentMoveRefusal(cache, cache.me, attachment, args.folderId);
  if (refusal !== null) {
    throw new Error(refusal);
  }
  const target = args.folderId === homeFolderId(attachment) ? null : args.folderId;
  if (attachmentFolderId(attachment) === target) {
    return;
  }
  attachment.folderId = target;
  bump(...attachmentOwnerKeys(cache, attachment, undefined));
}
